import cn from 'classnames';
import { ChangeEvent, useEffect, useState } from 'react';
import BaseModal from '@/components/modal/lib/BaseModal';
import Text from '@/components/common/Text/Text';

import './SizeSelectModal.style.scss';
import { ProductSizeVariant } from '@/types/Product';
import Select from '@/components/common/Select/Select';
import Button from '@/components/common/Button/Button';
import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner';
import { datadogLogs } from '@datadog/browser-logs';

type SizeSelectModalProps = {
  className?: string;
  onClose: () => void;
  allowSelectUndefined?: boolean;
  sizeVariants: (ProductSizeVariant & { isInStock: boolean })[];
  onSizeChange?: (selectedSize: ProductSizeVariant | undefined) => void;
  onSizeConfirm?: (
    selectedSize: ProductSizeVariant | undefined
  ) => Promise<void>;
  /** Diagnostic correlation id from the originating click. */
  clickId?: string;
};

function SizeSelectModal(props: SizeSelectModalProps) {
  const {
    className = '',
    onClose,
    allowSelectUndefined = false,
    sizeVariants,
    onSizeChange,
    onSizeConfirm,
    clickId
  } = props;

  useEffect(() => {
    datadogLogs.logger.info('size_modal_rendered', {
      category: 'add_to_cart_diagnostic',
      layer: 'SizeSelectModal',
      clickId: clickId ?? null,
      sizeVariantCount: sizeVariants.length,
      inStockCount: sizeVariants.filter((v) => v.isInStock).length,
      outOfStockCount: sizeVariants.filter((v) => !v.isInStock).length,
      sizeVariants
    });
  }, [clickId, sizeVariants]);
  const [value, setValue] = useState<ProductSizeVariant | undefined>();
  const [loading, setLoading] = useState<boolean>(false);

  const handleChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const addToCartUuid = event.currentTarget.value;

    const selectedVariant = sizeVariants.find((variant) => {
      return variant.addToCartUUID === addToCartUuid;
    });

    if (!addToCartUuid || !selectedVariant) {
      setValue(undefined);
      onSizeChange?.(undefined);
      return;
    }

    datadogLogs.logger.info('size_selected', {
      category: 'add_to_cart_diagnostic',
      layer: 'SizeSelectModal',
      clickId: clickId ?? null,
      addToCartUUID: selectedVariant.addToCartUUID,
      productVariantId: selectedVariant.productVariantId,
      sizeDescription: selectedVariant.sizeDescription,
      isInStock: selectedVariant.isInStock
    });

    setValue(selectedVariant);
    onSizeChange?.(selectedVariant);
  };

  const handleSelect = async () => {
    if (!value) {
      if (allowSelectUndefined) {
        setLoading(true);
        await onSizeConfirm?.(undefined);
        setLoading(false);
        return;
      }
      return;
    }

    setLoading(true);
    await onSizeConfirm?.(value);
    setLoading(false);
  };

  return (
    <BaseModal
      className={cn('SizeSelectModal', className)}
      onClose={onClose}
    >
      <Text
        className='SizeSelectModal-title'
        variant='title-xl'
        italic
      >
        Select a size
      </Text>
      <Select
        className='SizeSelectModal-Select'
        value={value?.addToCartUUID}
        onChange={handleChange}
      >
        <option>Select A Size</option>
        {sizeVariants.map((variant) => {
          return (
            <option
              key={variant.addToCartUUID}
              value={variant.addToCartUUID}
              disabled={!variant.isInStock}
            >
              {variant.sizeDescription}
            </option>
          );
        })}
      </Select>
      <Button
        className='SizeSelectModal-button'
        onClick={handleSelect}
        disabled={loading || !value}
      >
        {loading ? <LoadingSpinner size={10} /> : 'Add to closet'}
      </Button>
    </BaseModal>
  );
}

export default SizeSelectModal;
